import { useIsMobileLayout } from '@/shared/hooks/useMediaQuery';
import { useUIStore } from '@/stores/uiStore';
import { Link, useLocation } from '@tanstack/react-router';
import * as Collapsible from '@radix-ui/react-collapsible';
import { ChevronDown, X } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';
import {
  collectMenuPaths,
  isMenuPathActive,
} from '@/layouts/menuPathActive';
import type { MenuLink, SidebarMenuItem } from './sidebarMenuConfig';

interface SidebarMenuProps {
  items: SidebarMenuItem[];
  title?: string;
  subtitle?: string;
  /** 설정 종료 등 — 사이드바 하단 고정 영역 */
  footer?: ReactNode;
}

const SidebarMenu = ({
  items,
  title = 'RUDALINK',
  subtitle = 'Admin',
  footer,
}: SidebarMenuProps) => {
  const { sidebarCollapsed, mobileSidebarOpen, setMobileSidebarOpen } =
    useUIStore();
  const isMobile = useIsMobileLayout();
  // 모바일은 서랍으로 띄우므로 아이콘만 보이는 접힘 상태를 쓰지 않는다
  const collapsed = sidebarCollapsed && !isMobile;
  const location = useLocation();
  const allMenuPaths = useMemo(() => collectMenuPaths(items), [items]);

  const isActive = (path: string) =>
    isMenuPathActive(location.pathname, path, allMenuPaths);

  const activeGroupLabel = useMemo(() => {
    for (const item of items) {
      if ('type' in item && item.type === 'divider') continue;
      if ('children' in item && item.children.some((c) => isActive(c.key))) {
        return item.label;
      }
    }
    return null;
  }, [items, location.pathname, allMenuPaths]);

  // 대메뉴는 아코디언 — 한 그룹만 펼침. 현재 경로 그룹이 있으면 그쪽으로 맞춤.
  const [openGroupLabel, setOpenGroupLabel] = useState<string | null>(
    activeGroupLabel,
  );

  useEffect(() => {
    setOpenGroupLabel(activeGroupLabel);
  }, [location.pathname, activeGroupLabel]);

  const renderLink = (item: MenuLink) => {
    const Icon = item.icon;
    const active = isActive(item.key);

    return (
      <Link
        key={item.key}
        to={item.key}
        // TanStack Link 기본 fuzzy active(형제 `/admin/max-match/*` 오탐)를
        // 끄고, 형제 메뉴를 고려한 isMenuPathActive 결과만 하이라이트에 쓴다.
        activeProps={{}}
        activeOptions={{ exact: true, includeSearch: false }}
        data-active={active}
        className={cn(
          'ruda-menu-item flex h-9 items-center gap-2.5 rounded-12 px-2.5 text-sm font-medium text-slate-400 transition-colors hover:bg-white/5 hover:text-slate-200',
          active &&
            'bg-gradient-to-r from-primary/20 to-primary/10 text-white shadow-sm shadow-primary/10',
          collapsed && 'justify-center px-2',
        )}
      >
        <Icon className="h-4 w-4 shrink-0" />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </Link>
    );
  };

  // 모바일 서랍은 메뉴 이동·데스크톱 전환 시 닫는다
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname, isMobile, setMobileSidebarOpen]);

  useEffect(() => {
    if (!isMobile || !mobileSidebarOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileSidebarOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMobile, mobileSidebarOpen, setMobileSidebarOpen]);

  const drawerHidden = isMobile && !mobileSidebarOpen;

  return (
    <>
      {isMobile && mobileSidebarOpen ? (
        <div
          aria-hidden
          className="fixed inset-0 z-40 bg-[#131927]/40"
          onClick={() => setMobileSidebarOpen(false)}
        />
      ) : null}
      <aside
        aria-hidden={drawerHidden || undefined}
        className={cn(
          'ruda-sidebar flex h-full shrink-0 flex-col transition-all duration-200',
          isMobile
            ? cn(
                // 모바일: 화면 왼쪽에서 밀려 나오는 서랍
                'fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] shadow-xl',
                drawerHidden ? 'invisible -translate-x-full' : 'translate-x-0',
              )
            : collapsed
              ? 'w-16'
              : 'w-60',
        )}
      >
        <div
          className={cn(
            // 메인 헤더(모바일 h-16 · lg h-20)와 동일 높이 — 하단 보더 라인 정렬
            'flex h-16 shrink-0 items-center justify-between border-b border-white/8 xl:h-20',
            collapsed ? 'justify-center px-2' : 'px-3',
          )}
        >
          {!collapsed ? (
            <div className="flex min-w-0 flex-col gap-1">
              {/* 다크 사이드바용 화이트 워드마크 BI */}
              <img
                src="/logo/rudalink_bi_white.png"
                alt={title}
                className="h-7 w-auto max-w-[168px] object-contain object-left"
              />
              <div className="px-0.5 text-[11px] font-medium uppercase tracking-wider text-slate-500">
                {subtitle}
              </div>
            </div>
          ) : (
            <div className="flex h-9 w-9 items-center justify-center overflow-hidden">
              <img
                src="/logo/rudalink_bi_white.png"
                alt={title}
                className="h-7 w-7 object-left object-cover"
              />
            </div>
          )}
          {isMobile ? (
            <button
              type="button"
              aria-label="메뉴 닫기"
              onClick={() => setMobileSidebarOpen(false)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-12 text-slate-400 hover:bg-white/5 hover:text-slate-200"
            >
              <X className="h-5 w-5" />
            </button>
          ) : null}
        </div>

        <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-3">
          {items.map((item, index) => {
            if ('type' in item && item.type === 'divider') {
              return (
                <div
                  key={`divider-${index}`}
                  className="my-2 h-px bg-white/8"
                />
              );
            }

            if ('children' in item) {
              const GroupIcon = item.icon;
              const isOpen = openGroupLabel === item.label;

              return (
                <Collapsible.Root
                  key={item.label}
                  open={isOpen}
                  onOpenChange={(nextOpen) =>
                    setOpenGroupLabel(nextOpen ? item.label : null)
                  }
                >
                  <Collapsible.Trigger
                    className={cn(
                      'flex h-9 w-full items-center gap-2.5 rounded-12 px-2.5 text-sm font-medium text-slate-400 transition-colors hover:bg-white/5 hover:text-slate-200',
                      collapsed && 'justify-center',
                    )}
                  >
                    <GroupIcon className="h-4 w-4 shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate text-left">
                          {item.label}
                        </span>
                        <ChevronDown className="h-3.5 w-3.5 shrink-0 transition-transform duration-200 ease-out [[data-state=open]>&]:rotate-180" />
                      </>
                    )}
                  </Collapsible.Trigger>
                  {!collapsed && (
                    <Collapsible.Content className="ruda-sidebar-collapsible-content">
                      <div className="mt-0.5 flex flex-col gap-0.5 pl-2">
                        {item.children.map(renderLink)}
                      </div>
                    </Collapsible.Content>
                  )}
                </Collapsible.Root>
              );
            }

            return renderLink(item as MenuLink);
          })}
        </nav>

        {footer ? (
          <div
            className={cn(
              'shrink-0 border-t border-white/8 p-3',
              collapsed && 'flex justify-center',
            )}
          >
            {footer}
          </div>
        ) : null}
      </aside>
    </>
  );
};

export default SidebarMenu;
